import { ConsentActionService, ScheduledConsent } from '@csp/csp-common-consent';
import { ZonedDateTimeFactory, ZonedDateTimeFormatter } from '@csp/csp-common-date-util';
import { EnumUtil } from '@csp/csp-common-enum-util';
import { DmdpError, User } from '@csp/csp-common-model';
import { ConsentType } from '@csp/dmdp-api-user-dto';
import { ConsentForm } from '../model/ConsentForm';

const REJECTABLE_CONSENT_TYPES: string[] = [ConsentType.ANALYTICS] as const;
const WITHDRAWABLE_CONSENT_TYPES: string[] = [ConsentType.ANALYTICS] as const;

const from = (scheduledConsent: ScheduledConsent): ConsentForm => {
  const { artifact, consentActionStatus, consentType, version } = scheduledConsent;
  const approveConsent = async (): Promise<void> => {
    const now = ZonedDateTimeFactory.now();

    try {
      await ConsentActionService.approveConsentV2(
        artifact.artifactCode,
        ZonedDateTimeFormatter.toIsoFormattedString(now),
      );
    } catch (e) {
      if (DmdpError.isBadArgument(e) && e.message.endsWith('wrong state: APPROVED')) {
        // Ignore error when consent has already been approved. The reason for this
        // is that we don't care if it's already approved, since approving is the
        // action the user is taking and the outcome is the same.
        return;
      }
      throw e;
    }
  };

  const isRejectable = REJECTABLE_CONSENT_TYPES.includes(consentType);

  const rejectConsent = async (user: User): Promise<void> => {
    if (!isRejectable) {
      console.warn(`Trying to reject consent of a non rejectable consent type: ${consentType}`);
      return;
    }

    const now = ZonedDateTimeFactory.now();

    await ConsentActionService.rejectConsent(
      artifact.artifactCode,
      ZonedDateTimeFormatter.toIsoFormattedString(now),
      consentType,
      user,
    );
  };

  const isWithdrawable = WITHDRAWABLE_CONSENT_TYPES.includes(consentType);

  const withdrawConsent = async (user: User): Promise<void> => {
    if (!isWithdrawable) {
      console.warn(`Trying to withdraw consent of a non withdrawable consent type: ${consentType}`);
      return;
    }

    const now = ZonedDateTimeFactory.now();

    await ConsentActionService.withdrawConsent(
      artifact.artifactCode,
      ZonedDateTimeFormatter.toIsoFormattedString(now),
      consentType,
      user,
    );
  };

  return {
    approvalStatus: consentActionStatus.status,
    approvalStatusTimestamp: ZonedDateTimeFactory.fromZonedDateTimeString(consentActionStatus.statusUpdateTimestamp),
    approveConsent,
    rejectConsent,
    isRejectable,
    withdrawConsent,
    isWithdrawable,
    artifactCode: artifact.artifactCode,
    consentType: EnumUtil.fromMaybeStringOrDefault(consentType, ConsentType, ConsentType.UNKNOWN),
    version,
  };
};

export const ConsentFormMapper = {
  from,
};
